import { useMemo, useState } from 'react'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePSNavigate } from '../../hooks/usePSNavigate'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { isValidPostalCode } from '../../utils'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import { ACTION_BUTTON, OPTIONAL_BUTTON, WarningModal } from '../common/modals/WarningModal'
import { Checkbox } from '../common/fields/Checkbox'
import { TextField } from '../common/fields/TextField'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    title?: string
    onComplete?: (user?: User) => void
    onClose?: () => void
}

export function SetupLocationPage({ title, onComplete, onClose }: Props) {
    const originalMe = PSApplication.me()
    const [user, setUser] = useState(originalMe.clone())
    const [isSaving, setIsSaving] = useState(false)
    const { logAndDisplayError } = useErrorHandler()
    const navigate = usePSNavigate()

    const [modalState, setModalState] = useState<'warning_modal' | undefined>(undefined)
    const streetRequired = !originalMe.verifiedDate

    const postalCodeErrorState = useMemo(() => {
        return isValidPostalCode(user.postalCode) ? undefined : 'error'
    }, [user])

    const streetErrorState = useMemo(() => {
        return !streetRequired || user.line1?.replace(/\s/g, '').length ? undefined : 'error'
    }, [user])

    const hasEdits = useMemo(() => {
        return (
            user.line1 !== originalMe.line1 ||
            user.line2 !== originalMe.line2 ||
            user.city !== originalMe.city ||
            user.postalCode !== originalMe.postalCode ||
            user.vendorLocation !== originalMe.vendorLocation
        )
    }, [user])

    const [shouldShowErrors, setShouldShowErrors] = useState(false)

    const checkRecordPerm = streetErrorState === undefined && postalCodeErrorState === undefined

    const saveEnabled = useMemo(() => {
        return Boolean(
            hasEdits &&
                user.line1 &&
                user.postalCode &&
                user.postalCode.length === 5 &&
                !streetErrorState &&
                !postalCodeErrorState
        )
    }, [hasEdits, checkRecordPerm, streetErrorState, postalCodeErrorState])

    function closeModal() {
        setModalState(undefined)
    }

    async function submit() {
        setShouldShowErrors(true)

        if (streetErrorState || postalCodeErrorState) {
            return
        }

        if (originalMe.vendorLocation && !user.vendorLocation && user.allowProduct) {
            setModalState('warning_modal')
            return
        }

        await sendUser()

        setShouldShowErrors(false)

        if (onComplete) {
            return onComplete(user)
        }

        navigate('/settings')
    }

    const sendUser = async () => {
        setIsSaving(true)
        const result = await RecordFactory.getInstance().sendUser(user)
        setIsSaving(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
    }

    return (
        <WorkflowPage
            title={title ?? 'Location'}
            onClose={onClose}
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button
                        intent="primary"
                        disabled={!saveEnabled}
                        onClick={submit}
                        isLoading={isSaving}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start">
                <WorkflowPage.Card>
                    <WorkflowPage.IconSection title="Street (required)" icon="globeClock.large">
                        <TextField
                            size="medium"
                            placeholder="Enter street"
                            value={user.line1}
                            onClear={() => setUser(user.clone({ line1: '' }))}
                            onChange={(event) => setUser(user.clone({ line1: event.target.value }))}
                            hasError={!!streetErrorState && shouldShowErrors}
                            required={streetRequired}
                        />
                        {!!streetErrorState && shouldShowErrors && (
                            <Label size="sm" intent="danger">
                                A street address is required
                            </Label>
                        )}
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Unit" icon="hash.large">
                        <TextField
                            size="medium"
                            placeholder="Enter unit number"
                            value={user.line2}
                            onClear={() => setUser(user.clone({ line2: '' }))}
                            onChange={(event) => setUser(user.clone({ line2: event.target.value }))}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="City" icon="markerPin.large">
                        <TextField
                            size="medium"
                            placeholder="Enter city"
                            value={user.city}
                            onClear={() => setUser(user.clone({ city: '' }))}
                            onChange={(event) => setUser(user.clone({ city: event.target.value }))}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Zip code (required)" icon="globeClock.large">
                        <TextField
                            size="medium"
                            placeholder="Enter zip code"
                            mask="00000"
                            value={user.postalCode}
                            onClear={() => setUser(user.clone({ postal_code: '' }))}
                            onChange={(event) =>
                                setUser(user.clone({ postal_code: event.target.value }))
                            }
                            hasError={!!postalCodeErrorState && shouldShowErrors}
                        />
                        {!!postalCodeErrorState && shouldShowErrors && (
                            <Label size="sm" intent="danger">
                                That does not appear to be a valid zipcode.
                            </Label>
                        )}
                    </WorkflowPage.IconSection>

                    {(user.solo || user.company) && (
                        <WorkflowPage.IconSection
                            title="Schedule clients here"
                            icon="calendar.large"
                            hasHoverEffect
                            description="Enable this if you perform services at a fixed location or plan to allow pickup orders"
                            secondaryView={
                                <Checkbox
                                    isChecked={user.vendorLocation}
                                    onChange={(checked) => {
                                        setUser(user.clone({ location: checked }))
                                    }}
                                />
                            }
                        />
                    )}
                </WorkflowPage.Card>
            </div>

            {modalState === 'warning_modal' && (
                <WarningModal
                    title="Are you sure?"
                    message='For Pickup orders, please set a location and toggle on "Schedule clients here" or your product orders will only be available for Shipping.'
                    buttonText="Yes, Shipping only"
                    extraButtonText="Dismiss"
                    onClose={closeModal}
                    onButtonClick={(buttonIx) => {
                        if (buttonIx === ACTION_BUTTON) sendUser()
                        else if (buttonIx === OPTIONAL_BUTTON) closeModal()
                    }}
                />
            )}

            <NavigationBlockerPrompt
                when={hasEdits && !isSaving}
                allowCurrentPaths={[/\/onboarding.*/]}
            />
        </WorkflowPage>
    )
}
